import { STEPS, STEP_KEYS } from './constants';

/**
 * Step-navigation helpers over the STEPS registry (the single source of truth,
 * defined in constants.js). These answer "which steps are active", "what's
 * next", and "is this step conditional" so callers (prefetch, the quiz
 * progress bar, the drift-guard test) don't reimplement ordering logic.
 */

/** A step is conditional when it carries an isActive predicate. */
export const isConditionalStep = step => typeof step.isActive === 'function';

const INDEX_BY_KEY = STEP_KEYS.reduce((acc, key, i) => {
  acc[key] = i;
  return acc;
}, {});

/** Index of a step key in the ordered list, or -1 if it is not a milestone. */
export const getStepIndex = key =>
  key in INDEX_BY_KEY ? INDEX_BY_KEY[key] : -1;

/**
 * Milestone keys present for the given flags. Conditional steps whose isActive
 * predicate is false for these flags are dropped; everything else stays in
 * order. `flags` is shaped like the machine context's flags object.
 */
export const getActiveStepKeys = (flags = {}) =>
  STEPS.filter(
    step =>
      !isConditionalStep(step) || step.isActive({ flags }, { payload: {} }),
  ).map(step => step.key);

/**
 * The next milestone key(s) after `key`.
 *
 * Normally this is just the following step. When the following step is
 * conditional (may be skipped for some users) the step after it is included
 * too, so callers that warm / prefetch cover both branches without knowing the
 * flag. Returns [] for the last step or an unknown key.
 */
export const getNextStepKeys = key => {
  const i = getStepIndex(key);
  if (i < 0 || i >= STEPS.length - 1) return [];

  const next = STEPS[i + 1];
  const keys = [next.key];

  if (isConditionalStep(next) && STEPS[i + 2]) {
    keys.push(STEPS[i + 2].key);
  }

  return keys;
};
